
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { BlockMath } from "react-katex";
import Latex from "react-latex-next";
import "katex/dist/katex.min.css";
import "./MillionaireGame.css";

const MillionairePercentages2 = () => {
  // =========================================================
  // LATEX HELPERS
  // =========================================================

  const hasDelims = (s = "") =>
    /^\$[\s\S]*\$$/.test(s.trim()) ||
    /^\$\$[\s\S]*\$\$$/.test(s.trim()) ||
    /^\\\([\s\S]*\\\)$/.test(s.trim()) ||
    /^\\\[[\s\S]*\\\]$/.test(s.trim());

  const looksLikeLatex = (s = "") =>
    /\\[a-zA-Z]+|\\frac|\\sqrt|\\times|\\cdot|\\pounds|\\dfrac|[\_^]/.test(
      s
    ) || hasDelims(s);

  const inlineOrText = (s = "") =>
    hasDelims(s) ? s : `$${s}$`;

  const stripDelims = (s = "") =>
    s
      .replace(/^\s*(\$\$?|\\\[|\\\()/, "")
      .replace(/(\$\$?|\\\]|\\\))\s*$/, "");

  // =========================================================
  // GAME STATE
  // =========================================================

  const [gameOver, setGameOver] = useState({
    reached: false,
    amount: "",
    emoji: "",
    message: "",
    bitmoji: ""
  });

  const [showTitleScreen, setShowTitleScreen] = useState(true);

  const { assignmentId } = useParams();

  const [questions, setQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);

  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const [visibleOptions, setVisibleOptions] = useState([]);

  const [phoneFriendTip, setPhoneFriendTip] = useState("");
  const [audienceVotes, setAudienceVotes] = useState([]);
  const [fullAudienceVotes, setFullAudienceVotes] = useState([]);
  const [animateBars, setAnimateBars] = useState(false);

  const [lifelinesUsed, setLifelinesUsed] = useState({
    fiftyFifty: false,
    phoneFriend: false,
    askAudience: false
  });

  const [playerWorking, setPlayerWorking] = useState("");

  const [musicEnabled, setMusicEnabled] = useState(true);
  const [sfxEnabled, setSfxEnabled] = useState(true);

  // =========================================================
  // FINAL ANSWER STATE
  // =========================================================

  const [showFinalAnswerModal, setShowFinalAnswerModal] =
    useState(false);

  const [pendingFinalAnswer, setPendingFinalAnswer] =
    useState(null);

  const [answerLocked, setAnswerLocked] = useState(false);

  // =========================================================
  // CORRECT / WRONG ANIMATION STATE
  // =========================================================

  const [showCorrectTick, setShowCorrectTick] = useState(false);
  const [showWrongCross, setShowWrongCross] = useState(false);

  // =========================================================
  // AUDIO REFS
  // =========================================================

  const welcomeAudioRef = useRef(null);
  const roundAudioRef = useRef(null);
  const correctSoundRef = useRef(null);
  const wrongSoundRef = useRef(null);
  const fiftyFiftySoundRef = useRef(null);
  const phoneSoundRef = useRef(null);
  const takeMoneySoundRef = useRef(null);
  const winMusicRef = useRef(null);

  // =========================================================
  // MUSIC
  // =========================================================

  const getRoundMusic = (index) => {
    if (index < 5) return "/Millionaire/Round1.mp3";
    if (index < 10) return "/Millionaire/Round2.mp3";
    return "/Millionaire/Round3.mp3";
  };

  useEffect(() => {
    if (roundAudioRef.current) {
      roundAudioRef.current.volume = 0.1;
    }

    if (welcomeAudioRef.current) {
      welcomeAudioRef.current.volume = 0.1;
    }

    if (winMusicRef.current) {
      winMusicRef.current.volume = 0.1;
    }
  }, []);

  useEffect(() => {
    if (correctSoundRef.current) {
      correctSoundRef.current.volume = 0.1;
    }

    if (wrongSoundRef.current) {
      wrongSoundRef.current.volume = 0.2;
    }

    if (fiftyFiftySoundRef.current) {
      fiftyFiftySoundRef.current.volume = 0.2;
    }

    if (phoneSoundRef.current) {
      phoneSoundRef.current.volume = 0.1;
    }

    if (takeMoneySoundRef.current) {
      takeMoneySoundRef.current.volume = 0.2;
    }
  }, []);

  useEffect(() => {
    if (!showTitleScreen && roundAudioRef.current) {
      roundAudioRef.current.pause();

      roundAudioRef.current.src = getRoundMusic(currentQIndex);
      roundAudioRef.current.load();
      roundAudioRef.current.volume = 0.1;

      if (musicEnabled) {
        roundAudioRef.current.play().catch(() => {});
      }
    }
  }, [currentQIndex, showTitleScreen, musicEnabled]);

  // =========================================================
  // START GAME
  // =========================================================

  const startGame = () => {
    setShowTitleScreen(false);

    if (welcomeAudioRef.current) {
      welcomeAudioRef.current.pause();
      welcomeAudioRef.current.currentTime = 0;
    }
  };

  // =========================================================
  // MONEY LADDER
  // =========================================================

  const moneyLadder = [
    "£100",
    "£200",
    "£300",
    "£500",
    "£1,000",
    "£2,000",
    "£4,000",
    "£8,000",
    "£16,000",
    "£32,000",
    "£64,000",
    "£125,000",
    "£250,000",
    "£500,000",
    "⭐£1 MILLION⭐"
  ];

  // =========================================================
  // BITMOJIS
  // =========================================================

  const CRYING_BITMOJI =
    "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2Fbitmoji_cry.png?alt=media&token=247bf73a-1a2f-4f99-ad10-11da5a806ae8";

  const DISAPPOINTED_BITMOJI =
    "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2Fbitmoji_ok.png?alt=media&token=dec93d22-c6da-41aa-8f93-6568265ab063";

  const THUMBSUP_BITMOJI =
    "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2Fbitmoji_ok.png?alt=media&token=dec93d22-c6da-41aa-8f93-6568265ab063";

  const JACKPOT_BITMOJI =
    "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FBitmoji-jackpot.png?alt=media&token=a90935de-d124-4f80-a62c-b9c9174731e4";

  const getBitmojiForAmount = (amountStr) => {
    const amount = parseInt(
      amountStr.replace(/[^\d]/g, ""),
      10
    );

    if (amount <= 500) return CRYING_BITMOJI;
    if (amount <= 16000) return DISAPPOINTED_BITMOJI;

    return THUMBSUP_BITMOJI;
  };

  // =========================================================
  // QUESTIONS
  // =========================================================

  useEffect(() => {
    const buildQuestions = () => {
      const q = ({
        id,
        difficulty,
        paragraphs,
        options
      }) => ({
        questionId: id,
        text: "",
        latex: "",
        optionalQuestionText: "",
        answerPrefix: "",
        answerSuffix: "",
        paragraphs,
        correctAnswer:
          options.find((option) => option.isCorrect)?.text || "",
        options,
        difficulty
      });

      const shuffle = (arr) =>
        [...arr].sort(() => Math.random() - 0.5);

      const makeOptions = (correct, wrongs) =>
        shuffle([
          {
            text: correct,
            isCorrect: true
          },
          ...wrongs.slice(0, 3).map((wrong) => ({
            text: wrong,
            isCorrect: false
          }))
        ]);

      const Q1 = q({
        id: "percentages_01",
        difficulty: 1,
        paragraphs: [
          { text: "Increase £50 by 10%." }
        ],
        options: makeOptions("£55", [
          "£60",
          "£50.10",
          "£45"
        ])
      });

      const Q2 = q({
        id: "percentages_02",
        difficulty: 2,
        paragraphs: [
          { text: "Decrease £80 by 20%." }
        ],
        options: makeOptions("£64", [
          "£60",
          "£76",
          "£96"
        ])
      });

      const Q3 = q({
        id: "percentages_03",
        difficulty: 3,
        paragraphs: [
          { text: "A jacket costs £60." },
          { text: "Its price is reduced by 25%. What is the new price?" }
        ],
        options: makeOptions("£45", [
          "£35",
          "£48",
          "£75"
        ])
      });

      const Q4 = q({
        id: "percentages_04",
        difficulty: 4,
        paragraphs: [
          { text: "A worker earns £1,200 per month." },
          { text: "Their pay is increased by 5%. What is their new monthly pay?" }
        ],
        options: makeOptions("£1,260", [
          "£1,205",
          "£1,250",
          "£1,140"
        ])
      });

      const Q5 = q({
        id: "percentages_05",
        difficulty: 5,
        paragraphs: [
          { text: "A car is worth £2,500." },
          { text: "Its value decreases by 12%. What is it worth now?" }
        ],
        options: makeOptions("£2,200", [
          "£2,488",
          "£2,300",
          "£2,800"
        ])
      });

      const Q6 = q({
        id: "percentages_06",
        difficulty: 6,
        paragraphs: [
          { text: "The price of a ticket rises from £80 to £100." },
          { text: "What is the percentage increase?" }
        ],
        options: makeOptions("25%", [
          "20%",
          "80%",
          "125%"
        ])
      });

      const Q7 = q({
        id: "percentages_07",
        difficulty: 7,
        paragraphs: [
          { text: "A phone originally costs £240." },
          { text: "Its price is reduced to £204. What is the percentage decrease?" }
        ],
        options: makeOptions("15%", [
          "17.6%",
          "36%",
          "85%"
        ])
      });

      const Q8 = q({
        id: "percentages_08",
        difficulty: 8,
        paragraphs: [
          { text: "After a 20% increase, a monthly bill is £72." },
          { text: "What was the bill before the increase?" }
        ],
        options: makeOptions("£60", [
          "£57.60",
          "£52",
          "£86.40"
        ])
      });

      const Q9 = q({
        id: "percentages_09",
        difficulty: 9,
        paragraphs: [
          { text: "A bicycle is reduced by 25% in a sale." },
          { text: "The sale price is £90. What was the original price?" }
        ],
        options: makeOptions("£120", [
          "£112.50",
          "£115",
          "£67.50"
        ])
      });

      const Q10 = q({
        id: "percentages_10",
        difficulty: 10,
        paragraphs: [
          { text: "A computer costs £144 including VAT at 20%." },
          { text: "What was its price before VAT was added?" }
        ],
        options: makeOptions("£120", [
          "£115.20",
          "£124",
          "£172.80"
        ])
      });

      const Q11 = q({
        id: "percentages_11",
        difficulty: 11,
        paragraphs: [
          { text: "A shop buys a television for £250 and sells it for £310." },
          { text: "What is the percentage profit?" }
        ],
        options: makeOptions("24%", [
          "19.4%",
          "60%",
          "124%"
        ])
      });

      const Q12 = q({
        id: "percentages_12",
        difficulty: 12,
        paragraphs: [
          { text: "A sofa is reduced by 30% in a sale." },
          { text: "The sale price is £840. What was the original price?" }
        ],
        options: makeOptions("£1,200", [
          "£1,092",
          "£1,120",
          "£588"
        ])
      });

      const Q13 = q({
        id: "percentages_13",
        difficulty: 13,
        paragraphs: [
          { text: "A company's monthly energy use falls from 480 kWh to 396 kWh." },
          { text: "What is the percentage decrease?" }
        ],
        options: makeOptions("17.5%", [
          "21.2%",
          "84%",
          "82.5%"
        ])
      });

      const Q14 = q({
        id: "percentages_14",
        difficulty: 14,
        paragraphs: [
          { text: "A trader buys a bicycle for £480 and spends £60 repairing it." },
          { text: "The bicycle is then sold for £675. What is the percentage profit?" }
        ],
        options: makeOptions("25%", [
          "20%",
          "28.1%",
          "40.6%"
        ])
      });

      const Q15 = q({
        id: "percentages_15",
        difficulty: 15,
        paragraphs: [
          { text: "£1 MILLION QUESTION" },
          { text: "A shop adds 20% VAT to an item's original price." },
          { text: "It then gives a 15% discount on the VAT-inclusive price." },
          { text: "The customer pays £306. What was the price before VAT was added?" }
        ],
        options: makeOptions("£300", [
          "£306",
          "£255",
          "£318.75"
        ])
      });

      return [
        Q1,
        Q2,
        Q3,
        Q4,
        Q5,
        Q6,
        Q7,
        Q8,
        Q9,
        Q10,
        Q11,
        Q12,
        Q13,
        Q14,
        Q15
      ];
    };

    setQuestions(buildQuestions());
  }, [assignmentId]);

  // =========================================================
  // RESET WHEN QUESTION CHANGES
  // =========================================================

  useEffect(() => {
    if (
      questions.length > 0 &&
      questions[currentQIndex]
    ) {
      setVisibleOptions(
        questions[currentQIndex].options
      );

      setPhoneFriendTip("");
      setAudienceVotes([]);
      setFullAudienceVotes([]);

      setPlayerWorking("");

      setSelectedOption(null);

      setPendingFinalAnswer(null);
      setShowFinalAnswerModal(false);

      setAnswerLocked(false);

      setShowResult(false);
      setIsCorrect(false);

      setShowCorrectTick(false);
      setShowWrongCross(false);
    }
  }, [questions, currentQIndex]);

  // =========================================================
  // 50:50
  // =========================================================

  const handleFiftyFifty = () => {
    if (
      lifelinesUsed.fiftyFifty ||
      !questions[currentQIndex] ||
      showResult ||
      showFinalAnswerModal ||
      answerLocked
    ) {
      return;
    }

    if (
      fiftyFiftySoundRef.current &&
      sfxEnabled
    ) {
      fiftyFiftySoundRef.current.currentTime = 0;
      fiftyFiftySoundRef.current.play();
    }

    const correctOption =
      visibleOptions.find(
        (option) => option.isCorrect
      );

    const incorrectOptions =
      visibleOptions.filter(
        (option) => !option.isCorrect
      );

    const randomIncorrect =
      [...incorrectOptions]
        .sort(() => Math.random() - 0.5)
        .slice(0, 1);

    const newVisible = [
      correctOption,
      ...randomIncorrect
    ].sort(() => Math.random() - 0.5);

    setVisibleOptions(newVisible);

    setLifelinesUsed((prev) => ({
      ...prev,
      fiftyFifty: true
    }));
  };

  // =========================================================
  // PHONE A FRIEND
  // =========================================================

  const handlePhoneFriend = () => {
    if (
      lifelinesUsed.phoneFriend ||
      !questions[currentQIndex] ||
      showResult ||
      showFinalAnswerModal ||
      answerLocked
    ) {
      return;
    }

    if (
      phoneSoundRef.current &&
      sfxEnabled
    ) {
      phoneSoundRef.current.currentTime = 0;
      phoneSoundRef.current.play();
    }

    const options = visibleOptions;

    const correctOption =
      options.find(
        (option) => option.isCorrect
      );

    const incorrectOptions =
      options.filter(
        (option) => !option.isCorrect
      );

    const accuracy =
      0.9 -
      currentQIndex * (0.6 / 14);

    const guessCorrect =
      Math.random() < accuracy;

    let friendGuess;

    if (
      guessCorrect ||
      incorrectOptions.length === 0
    ) {
      friendGuess = correctOption;
    } else {
      friendGuess =
        incorrectOptions[
          Math.floor(
            Math.random() *
              incorrectOptions.length
          )
        ];
    }

    if (friendGuess) {
      setPhoneFriendTip(
        friendGuess.text
      );
    }

    setLifelinesUsed((prev) => ({
      ...prev,
      phoneFriend: true
    }));
  };

  // =========================================================
  // ASK THE AUDIENCE
  // =========================================================

  const handleAskAudience = () => {
    if (
      lifelinesUsed.askAudience ||
      !questions[currentQIndex] ||
      showResult ||
      showFinalAnswerModal ||
      answerLocked
    ) {
      return;
    }

    const options =
      questions[currentQIndex].options;

    const correctIndex =
      options.findIndex(
        (option) => option.isCorrect
      );

    const baseAccuracy =
      0.95 -
      currentQIndex * (0.6 / 14);

    const votes =
      new Array(options.length).fill(0);

    votes[correctIndex] =
      Math.floor(
        baseAccuracy * 100
      );

    let remaining =
      100 - votes[correctIndex];

    const indices =
      options
        .map((_, index) => index)
        .filter(
          (index) =>
            index !== correctIndex
        );

    while (
      remaining > 0 &&
      indices.length > 0
    ) {
      const index =
        indices[
          Math.floor(
            Math.random() *
              indices.length
          )
        ];

      votes[index] += 1;
      remaining -= 1;
    }

    setFullAudienceVotes(votes);
    setAnimateBars(false);

    setTimeout(() => {
      setAnimateBars(true);
    }, 50);

    setAudienceVotes(votes);

    setLifelinesUsed((prev) => ({
      ...prev,
      askAudience: true
    }));
  };

  // =========================================================
  // TAKE THE MONEY
  // =========================================================

  const handleTakeTheMoney = () => {
    if (
      showFinalAnswerModal ||
      answerLocked
    ) {
      return;
    }

    if (roundAudioRef.current) {
      roundAudioRef.current.pause();
    }

    if (
      takeMoneySoundRef.current &&
      sfxEnabled
    ) {
      takeMoneySoundRef.current.currentTime = 0;
      takeMoneySoundRef.current.play();
    }

    const amount =
      moneyLadder[currentQIndex - 1] || "£0";

    const bitmoji =
      getBitmojiForAmount(amount);

    setGameOver({
      reached: true,
      amount,
      emoji: "💼",
      message:
        `You took the money and leave with ${amount}!`,
      bitmoji
    });
  };

  // =========================================================
  // ANSWER CLICK
  // =========================================================

  const handleAnswerClick = (option) => {
    if (
      showResult ||
      showFinalAnswerModal ||
      answerLocked
    ) {
      return;
    }

    // Questions 1–5 reveal immediately.
    if (currentQIndex < 5) {
      revealAnswer(option);
      return;
    }

    // Questions 6–15 require final-answer confirmation.
    setSelectedOption(option);
    setPendingFinalAnswer(option);
    setShowFinalAnswerModal(true);
  };

  // =========================================================
  // CONFIRM FINAL ANSWER
  // =========================================================

  const confirmFinalAnswer = () => {
    if (!pendingFinalAnswer) {
      return;
    }

    const option =
      pendingFinalAnswer;

    setShowFinalAnswerModal(false);
    setPendingFinalAnswer(null);

    setSelectedOption(option);

    // Orange lock-in period.
    setAnswerLocked(true);
    setShowResult(false);

    // Wait 2 seconds before revealing.
    setTimeout(() => {
      setAnswerLocked(false);
      revealAnswer(option);
    }, 2000);
  };

  // =========================================================
  // CANCEL FINAL ANSWER
  // =========================================================

  const cancelFinalAnswer = () => {
    setShowFinalAnswerModal(false);
    setPendingFinalAnswer(null);
    setSelectedOption(null);
  };

  // =========================================================
  // REVEAL ANSWER
  // =========================================================

  const revealAnswer = (option) => {
    const isRight =
      option?.isCorrect;

    setSelectedOption(option);
    setIsCorrect(isRight);
    setShowResult(true);

    // =======================================================
    // CORRECT ANSWER
    // =======================================================

    if (isRight) {
      setShowCorrectTick(true);
      setShowWrongCross(false);

      if (
        correctSoundRef.current &&
        sfxEnabled
      ) {
        correctSoundRef.current.currentTime = 0;
        correctSoundRef.current.play();
      }

      setTimeout(() => {
        setShowCorrectTick(false);
      }, 1300);
    }

    // =======================================================
    // WRONG ANSWER
    // =======================================================

    if (!isRight) {
      setShowWrongCross(true);
      setShowCorrectTick(false);

      if (
        wrongSoundRef.current &&
        sfxEnabled
      ) {
        wrongSoundRef.current.currentTime = 0;
        wrongSoundRef.current.play();
      }

      setTimeout(() => {
        setShowWrongCross(false);
      }, 1300);
    }

    // =======================================================
    // MOVE ON / GAME OVER
    // =======================================================

    setTimeout(() => {
      if (!isRight) {
        if (roundAudioRef.current) {
          roundAudioRef.current.pause();
        }

        const result =
          currentQIndex <= 4
            ? {
                amount: "£0",
                emoji: "💥",
                message:
                  "You got a question wrong and go home with £0.",
                bitmoji:
                  CRYING_BITMOJI
              }
            : currentQIndex <= 9
              ? {
                  amount: "£1,000",
                  emoji: "😢",
                  message:
                    "You leave with £1,000!",
                  bitmoji:
                    DISAPPOINTED_BITMOJI
                }
              : {
                  amount: "£32,000",
                  emoji: "😢",
                  message:
                    "You leave with £32,000!",
                  bitmoji:
                    THUMBSUP_BITMOJI
                };

        setGameOver({
          reached: true,
          ...result
        });

        return;
      }

      // =====================================================
      // £1 MILLION WIN
      // =====================================================

      if (currentQIndex === 14) {
        if (roundAudioRef.current) {
          roundAudioRef.current.pause();
        }

        if (
          musicEnabled &&
          winMusicRef.current
        ) {
          winMusicRef.current.currentTime = 0;
          winMusicRef.current.play();
        }

        setGameOver({
          reached: true,
          amount: "£1,000,000",
          emoji: "🎉",
          message:
            "You leave with £1,000,000!",
          bitmoji:
            JACKPOT_BITMOJI
        });

        return;
      }

      setShowResult(false);
      setSelectedOption(null);

      setShowCorrectTick(false);
      setShowWrongCross(false);

      setCurrentQIndex(
        (prev) => prev + 1
      );
    }, 1500);
  };

  // =========================================================
  // QUESTION RENDERING
  // =========================================================

  const renderQuestionContent = (
    question
  ) => {
    const {
      paragraphs = [],
      text = "",
      latex = "",
      optionalQuestionText = ""
    } = question;

    const isImg = (s) =>
      /^https?:\/\/.+\.(png|jpe?g|webp|gif|svg)$/i.test(
        s || ""
      );

    const isPureMathLine = (
      s = ""
    ) => {
      const t = s.trim();

      if (!t) return false;

      const withoutCommands =
        t.replace(
          /\\[a-zA-Z]+/g,
          ""
        );

      return !/[a-zA-Z]{2,}/.test(
        withoutCommands
      );
    };

    const renderMixed = (
      s = ""
    ) => {
      if (looksLikeLatex(s)) {
        return (
          <Latex>
            {inlineOrText(s)}
          </Latex>
        );
      }

      return <span>{s}</span>;
    };

    if (paragraphs.length > 0) {
      return paragraphs.map(
        (raw, i) => {
          let p = raw;

          if (
            typeof p === "string"
          ) {
            try {
              const maybe =
                JSON.parse(p);

              if (
                maybe &&
                (
                  maybe.text ||
                  maybe.image
                )
              ) {
                p = maybe;
              }
            } catch {
              // Leave as string.
            }
          }

          return (
            <div
              key={i}
              style={{
                marginBottom:
                  "1rem",
                lineHeight: 1.6,
                textAlign:
                  "center"
              }}
            >
              {typeof p === "string" ? (
                isImg(p) ? (
                  <img
                    src={p}
                    alt={`Question item ${i + 1}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 250,
                      display: "block",
                      margin: "0 auto"
                    }}
                  />
                ) : looksLikeLatex(p) ? (
                  <BlockMath
                    math={
                      stripDelims(p)
                    }
                  />
                ) : (
                  <div>{p}</div>
                )
              ) : (
                <>
                  {p.text && (
                    <div>
                      {isPureMathLine(
                        p.text
                      ) ? (
                        <div
                          style={{
                            display:
                              "flex",
                            justifyContent:
                              "center",
                            width: "100%",
                            margin:
                              "1.5rem 0",
                            fontSize: "3rem"
                          }}
                        >
                          <BlockMath
                            math={
                              stripDelims(
                                p.text
                              )
                            }
                          />
                        </div>
                      ) : (
                        <p
                          style={{
                            margin:
                              "0.5rem 0",
                            fontSize:
                              "1.3rem",
                            textAlign:
                              "center"
                          }}
                        >
                          {renderMixed(
                            p.text
                          )}
                        </p>
                      )}
                    </div>
                  )}

                  {p.image && (
                    <img
                      src={p.image}
                      alt={`Question item ${i + 1}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: 250,
                        display: "block",
                        margin:
                          "0.5rem auto 0"
                      }}
                    />
                  )}
                </>
              )}
            </div>
          );
        }
      );
    }

    return (
      <div
        style={{
          textAlign:
            "center",
          lineHeight: 1.6
        }}
      >
        {text &&
          text
            .split("\n")
            .map(
              (line, i) => (
                <div
                  key={i}
                  style={{
                    marginBottom:
                      "0.5rem",
                    textAlign:
                      "center"
                  }}
                >
                  {looksLikeLatex(line) ? (
                    <Latex>
                      {inlineOrText(
                        line
                      )}
                    </Latex>
                  ) : (
                    <span>{line}</span>
                  )}
                </div>
              )
            )}

        {latex && (
          <div
            style={{
              display: "flex",
              justifyContent:
                "center",
              margin:
                "0.5rem 0"
            }}
          >
            <BlockMath
              math={
                stripDelims(
                  latex
                )
              }
            />
          </div>
        )}

        {optionalQuestionText && (
          <div
            style={{
              marginTop:
                "0.5rem",
              fontWeight:
                "bold"
            }}
          >
            {looksLikeLatex(
              optionalQuestionText
            ) ? (
              <Latex>
                {inlineOrText(
                  optionalQuestionText
                )}
              </Latex>
            ) : (
              <span>
                {
                  optionalQuestionText
                }
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const currentQuestion =
    questions[currentQIndex];

  // =========================================================
  // MAIN JSX
  // =========================================================

  return (
    <>
      {showTitleScreen ? (
        // ===================================================
        // TITLE SCREEN
        // ===================================================

        <div className="welcome-screen">
          {musicEnabled && (
            <audio
              ref={welcomeAudioRef}
              src="/Millionaire/Who_Wants_To_Be_A_Millionaire_Theme.mp3"
              autoPlay
              loop
            />
          )}

          <img
            src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FBitmoji_wave.png?alt=media&token=e693ff6e-4c37-4fa4-9feb-338819589d7e"
            alt="Bitmoji"
            className="bitmoji"
          />

          <div className="logo-wrapper">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FMillionaire%20Logo.png?alt=media&token=64690774-c189-4a45-8b88-4eabf0ed0a55"
              alt="Millionaire Logo"
              className="millionaire-logo"
            />
          </div>

          <button
            className="start-btn"
            onClick={startGame}
          >
            Start Game
          </button>
        </div>
      ) : (
        <>
          <audio ref={roundAudioRef} />

          <div className="millionaire-wrapper">
            {!gameOver.reached && (
              <>
                {/* ===========================================
                    AUDIO CONTROLS
                    =========================================== */}

                <div
                  className="audio-toggles"
                  style={{
                    textAlign:
                      "center",
                    marginBottom:
                      "1rem"
                  }}
                >
                  <button
                    onClick={() =>
                      setMusicEnabled(
                        (prev) => !prev
                      )
                    }
                  >
                    🎵 Music:{" "}
                    {musicEnabled
                      ? "ON"
                      : "OFF"}
                  </button>

                  <button
                    onClick={() =>
                      setSfxEnabled(
                        (prev) => !prev
                      )
                    }
                    style={{
                      marginLeft:
                        "1rem"
                    }}
                  >
                    🔊 Sound FX:{" "}
                    {sfxEnabled
                      ? "ON"
                      : "OFF"}
                  </button>
                </div>

                {/* ===========================================
                    TAKE THE MONEY
                    =========================================== */}

                <div
                  style={{
                    textAlign:
                      "center",
                    marginBottom:
                      "1rem"
                  }}
                >
                  <button
                    className="take-money-btn"
                    onClick={
                      handleTakeTheMoney
                    }
                  >
                    💼 Take the Money
                  </button>
                </div>

                {/* ===========================================
                    LIFELINES
                    =========================================== */}

                <div className="lifelines">
                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2F50-50.gif?alt=media&token=4453e297-63ec-4e3d-9be2-9716a38f580f"
                    alt="50:50"
                    className={`lifeline-icon ${
                      lifelinesUsed.fiftyFifty
                        ? "used"
                        : ""
                    }`}
                    onClick={
                      handleFiftyFifty
                    }
                  />

                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FPhone%20a%20Friend.gif?alt=media&token=b2a4f76a-5b0b-4cfb-91fd-0292d4d24ca9"
                    alt="Phone a Friend"
                    className={`lifeline-icon ${
                      lifelinesUsed.phoneFriend
                        ? "used"
                        : ""
                    }`}
                    onClick={
                      handlePhoneFriend
                    }
                  />

                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FAsk%20The%20Audience.gif?alt=media&token=45572f27-6256-47b0-ad60-883b9486542b"
                    alt="Ask the Audience"
                    className={`lifeline-icon ${
                      lifelinesUsed.askAudience
                        ? "used"
                        : ""
                    }`}
                    onClick={
                      handleAskAudience
                    }
                  />
                </div>
              </>
            )}

            {/* ===============================================
                PHONE A FRIEND
                =============================================== */}

            {phoneFriendTip && (
              <div className="lifeline-tip">
                <p>
                  📞 Your friend thinks
                  the answer is:
                </p>

                <BlockMath
                  math={
                    phoneFriendTip
                  }
                />
              </div>
            )}

            {/* ===============================================
                GAME OVER
                =============================================== */}

            {gameOver.reached ? (
              <div className="game-over-screen fade-in">
                <h2>
                  {gameOver.emoji}{" "}
                  Game Over!
                </h2>

                <p>
                  {
                    gameOver.message
                  }
                </p>

                <img
                  src={
                    gameOver.bitmoji
                  }
                  alt="Bitmoji"
                  className="bitmoji-sad"
                />

                <button
                  className="try-again-btn"
                  onClick={() =>
                    window.location.reload()
                  }
                >
                  🔁 Try Again
                </button>
              </div>
            ) : currentQIndex >=
              questions.length ? (
              <h2>
                🏆 You've finished
                the game!
              </h2>
            ) : currentQuestion ? (
              // =============================================
              // GAME LAYOUT
              // =============================================

              <div className="game-layout">
                {/* ===========================================
                    WORKING BOX
                    =========================================== */}

                <div
                  className="working-box"
                  style={{
                    flex:
                      "0 0 250px",
                    background:
                      "#0d1b2a",
                    padding:
                      "1rem",
                    borderRadius:
                      "12px",
                    color: "white",
                    minHeight:
                      "250px",
                    transform:
                      "translateX(-230px)"
                  }}
                >
                  <h3
                    style={{
                      fontSize:
                        "1.1rem",
                      marginBottom:
                        "0.5rem"
                    }}
                  >
                    📝 Show Your
                    Working
                  </h3>

                  <textarea
                    placeholder="Write your working here..."
                    value={
                      playerWorking
                    }
                    onChange={(e) =>
                      setPlayerWorking(
                        e.target.value
                      )
                    }
                    disabled={
                      answerLocked
                    }
                    style={{
                      width: "100%",
                      height:
                        "200px",
                      borderRadius:
                        "8px",
                      padding:
                        "0.5rem",
                      resize:
                        "vertical",
                      fontFamily:
                        "inherit",
                      backgroundColor:
                        "#1c3a6b",
                      color: "white"
                    }}
                  />
                </div>

                {/* ===========================================
                    QUESTION BLOCK
                    =========================================== */}

                <div
                  key={
                    currentQIndex
                  }
                  className={`question-block animated ${
                    showResult &&
                    !isCorrect &&
                    currentQIndex <= 4
                      ? "fade-out"
                      : ""
                  }`}
                  style={{
                    marginLeft:
                      "-225px"
                  }}
                >
                  <h2>
                    Question{" "}
                    {currentQIndex +
                      1}
                  </h2>

                  <div className="question-content">
                    {renderQuestionContent(
                      currentQuestion
                    )}
                  </div>

                  {/* =========================================
                      ANSWER OPTIONS
                      ========================================= */}

                  <div className="options-grid">
                    {visibleOptions.map(
                      (
                        option,
                        index
                      ) => {
                        const isThisSelected =
                          selectedOption ===
                          option;

                        const isOrangeLocked =
                          isThisSelected &&
                          answerLocked &&
                          !showResult;

                        let resultClass =
                          "";

                        if (
                          isThisSelected &&
                          showResult
                        ) {
                          resultClass =
                            isCorrect
                              ? "correct"
                              : "wrong";
                        }

                        return (
                          <button
                            key={
                              index
                            }
                            className={`option-btn ${resultClass}`}
                            onClick={() =>
                              handleAnswerClick(
                                option
                              )
                            }
                            disabled={
                              showResult ||
                              showFinalAnswerModal ||
                              answerLocked
                            }
                            style={{
                              position:
                                "relative",

                              ...(isOrangeLocked
                                ? {
                                    background:
                                      "#f59e0b",
                                    borderColor:
                                      "#fbbf24",
                                    color:
                                      "#111827",
                                    boxShadow:
                                      "0 0 18px rgba(245, 158, 11, 0.8)",
                                    transform:
                                      "scale(1.03)",
                                    transition:
                                      "all 0.25s ease"
                                  }
                                : {})
                            }}
                          >
                            <BlockMath
                              math={
                                option.text
                              }
                            />

                            {/* =================================
                                CORRECT TICK
                                ================================= */}

                            {showCorrectTick &&
                              showResult &&
                              isCorrect &&
                              isThisSelected && (
                                <span className="answer-correct-tick">
                                  <svg
                                    viewBox="0 0 60 60"
                                    aria-hidden="true"
                                  >
                                    <circle
                                      className="answer-tick-circle"
                                      cx="30"
                                      cy="30"
                                      r="26"
                                    />

                                    <path
                                      className="answer-tick-path"
                                      d="M16 31 L25 40 L44 20"
                                    />
                                  </svg>
                                </span>
                              )}

                            {/* =================================
                                WRONG CROSS
                                ================================= */}

                            {showWrongCross &&
                              showResult &&
                              !isCorrect &&
                              isThisSelected && (
                                <span className="answer-wrong-cross">
                                  <svg
                                    viewBox="0 0 60 60"
                                    aria-hidden="true"
                                  >
                                    <circle
                                      className="answer-cross-circle"
                                      cx="30"
                                      cy="30"
                                      r="26"
                                    />

                                    <path
                                      className="answer-cross-path answer-cross-line-1"
                                      d="M19 19 L41 41"
                                    />

                                    <path
                                      className="answer-cross-path answer-cross-line-2"
                                      d="M41 19 L19 41"
                                    />
                                  </svg>
                                </span>
                              )}
                          </button>
                        );
                      }
                    )}
                  </div>

                  {/* =========================================
                      ASK THE AUDIENCE
                      ========================================= */}

                  {fullAudienceVotes.length >
                    0 &&
                    visibleOptions.length >
                      0 && (
                      <div
                        style={{
                          marginTop:
                            "1rem",
                          display:
                            "flex",
                          justifyContent:
                            "center"
                        }}
                      >
                        <div
                          style={{
                            display:
                              "flex",
                            flexDirection:
                              "column",
                            alignItems:
                              "center"
                          }}
                        >
                          <p
                            style={{
                              fontWeight:
                                "bold",
                              textAlign:
                                "center",
                              marginBottom:
                                "0.75rem",
                              fontSize:
                                "1.1rem"
                            }}
                          >
                            📊 Ask the
                            Audience
                          </p>

                          <div
                            style={{
                              display:
                                "flex",
                              alignItems:
                                "flex-end",
                              justifyContent:
                                "center",
                              gap:
                                "2rem"
                            }}
                          >
                            {visibleOptions.map(
                              (
                                option,
                                index
                              ) => {
                                const indexInFull =
                                  questions[
                                    currentQIndex
                                  ].options.findIndex(
                                    (
                                      fullOption
                                    ) =>
                                      fullOption.text ===
                                      option.text
                                  );

                                const percent =
                                  fullAudienceVotes[
                                    indexInFull
                                  ] || 0;

                                const height =
                                  percent *
                                  2;

                                return (
                                  <div
                                    key={
                                      index
                                    }
                                    style={{
                                      textAlign:
                                        "center"
                                    }}
                                  >
                                    <div
                                      style={{
                                        marginBottom:
                                          "0.25rem",
                                        fontWeight:
                                          "bold"
                                      }}
                                    >
                                      {
                                        percent
                                      }
                                      %
                                    </div>

                                    <div
                                      className={`audience-bar-fill ${
                                        animateBars
                                          ? "filled"
                                          : ""
                                      }`}
                                      style={{
                                        height: `${
                                          animateBars
                                            ? height
                                            : 0
                                        }px`
                                      }}
                                    />

                                    <div
                                      style={{
                                        marginTop:
                                          "0.5rem"
                                      }}
                                    >
                                      <BlockMath
                                        math={
                                          option.text
                                        }
                                      />
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* No Correct/Wrong text needed.
                      The green/red button,
                      animated tick/cross,
                      and sound provide feedback. */}
                </div>

                {/* ===========================================
                    MONEY LADDER
                    =========================================== */}

                <div className="money-ladder">
                  {[...moneyLadder]
                    .reverse()
                    .map(
                      (
                        amount,
                        index
                      ) => {
                        const level =
                          moneyLadder.length -
                          1 -
                          index;

                        return (
                          <div
                            key={
                              amount
                            }
                            className={`ladder-step ${
                              level ===
                              currentQIndex -
                                1
                                ? "active-step"
                                : ""
                            }`}
                          >
                            {
                              amount
                            }
                          </div>
                        );
                      }
                    )}
                </div>
              </div>
            ) : (
              <p>
                Loading game...
              </p>
            )}
          </div>
        </>
      )}

      {/* =====================================================
          FINAL ANSWER MODAL
          QUESTIONS 6–15
          ===================================================== */}

      {showFinalAnswerModal &&
        pendingFinalAnswer && (
          <div
            style={{
              position:
                "fixed",
              inset: 0,
              background:
                "rgba(0, 0, 0, 0.72)",
              zIndex: 9999,
              display:
                "flex",
              justifyContent:
                "center",
              alignItems:
                "center",
              padding:
                "1rem"
            }}
          >
            <div
              style={{
                width:
                  "min(520px, 92vw)",
                background:
                  "linear-gradient(180deg, #101b4c 0%, #080d2b 100%)",
                border:
                  "3px solid #f59e0b",
                borderRadius:
                  "20px",
                padding:
                  "2rem",
                textAlign:
                  "center",
                color:
                  "white",
                boxShadow:
                  "0 20px 60px rgba(0, 0, 0, 0.55)"
              }}
            >
              <div
                style={{
                  fontSize:
                    "2.5rem",
                  marginBottom:
                    "0.5rem"
                }}
              >
                🔒
              </div>

              <h2
                style={{
                  margin:
                    "0 0 1rem",
                  color:
                    "#fbbf24",
                  fontSize:
                    "2rem"
                }}
              >
                Is that your final
                answer?
              </h2>

              <p
                style={{
                  marginBottom:
                    "0.25rem",
                  opacity:
                    0.85
                }}
              >
                You have selected:
              </p>

              <div
                style={{
                  background:
                    "rgba(255,255,255,0.08)",
                  border:
                    "2px solid #6366f1",
                  borderRadius:
                    "12px",
                  margin:
                    "1rem 0 1.5rem",
                  padding:
                    "0.75rem",
                  fontSize:
                    "1.25rem"
                }}
              >
                <BlockMath
                  math={
                    pendingFinalAnswer.text
                  }
                />
              </div>

              <div
                style={{
                  display:
                    "flex",
                  gap:
                    "1rem",
                  justifyContent:
                    "center",
                  flexWrap:
                    "wrap"
                }}
              >
                <button
                  onClick={
                    cancelFinalAnswer
                  }
                  style={{
                    border:
                      "none",
                    borderRadius:
                      "10px",
                    padding:
                      "0.9rem 1.3rem",
                    fontSize:
                      "1rem",
                    fontWeight:
                      "bold",
                    cursor:
                      "pointer",
                    background:
                      "#475569",
                    color:
                      "white",
                    minWidth:
                      "170px"
                  }}
                >
                  ↩️ No, change answer
                </button>

                <button
                  onClick={
                    confirmFinalAnswer
                  }
                  style={{
                    border:
                      "none",
                    borderRadius:
                      "10px",
                    padding:
                      "0.9rem 1.3rem",
                    fontSize:
                      "1rem",
                    fontWeight:
                      "bold",
                    cursor:
                      "pointer",
                    background:
                      "#f59e0b",
                    color:
                      "#111827",
                    minWidth:
                      "170px"
                  }}
                >
                  🔒 Yes, final answer
                </button>
              </div>
            </div>
          </div>
        )}

      {/* =====================================================
          AUDIO FILES
          ===================================================== */}

      <audio
        ref={correctSoundRef}
        src="/Millionaire/correct2.mp3"
        preload="auto"
      />

      <audio
        ref={wrongSoundRef}
        src="/Millionaire/wrong.mp3"
        preload="auto"
      />

      <audio
        ref={fiftyFiftySoundRef}
        src="/Millionaire/fiftyfifty.mp3"
        preload="auto"
      />

      <audio
        ref={phoneSoundRef}
        src="/Millionaire/phoneafriend3.mp3"
        preload="auto"
      />

      <audio
        ref={takeMoneySoundRef}
        src="/Millionaire/wrong.mp3"
        preload="auto"
      />

      <audio
        ref={winMusicRef}
        src="/Millionaire/win.mp3"
        preload="auto"
      />
    </>
  );
};

export default MillionairePercentages2;
